/**
 * User ID cache — avoids redundant supabase.auth.getUser() network calls.
 *
 * Supabase's getUser() hits the auth server every time. In dataApi we call
 * it on every CRUD operation (createCard, createDeck, updateUserSettings,
 * etc.). This module caches the userId after first fetch and exposes an
 * explicit invalidation hook for sign-out and for adoption of a different account
 * (AuthContext), which also orphans any request still in flight.
 *
 * Trade-off: if the user's session is revoked server-side (e.g. admin
 * force-logout), the cache will return stale data until the next page
 * refresh or SIGNED_OUT event. In practice Supabase RLS still guards
 * the DB, so the worst case is a 403 from Postgres, not data leakage.
 */

import { supabase } from '@/lib/supabase';

let cachedUserId: string | null = null;

/**
 * Bumped by every invalidation. A getUser() answer is only trusted if the generation it started
 * in is still current: without this, a request begun for account A that lands after account B
 * took over would write A back into the cache and be returned as the valid actor (Vidi-atl.22.2).
 */
let generation = 0;
/** One request per generation, shared by concurrent callers. */
let flight: { generation: number; promise: Promise<string | null> } | null = null;

/** A change of actor mid-request is retried against the current generation, then fails safe. */
const MAX_ATTEMPTS = 3;

/** Resolves null when the generation changed while the request was away (the answer is stale). */
function fetchUserId(startedIn: number): Promise<string | null> {
  const promise = (async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (startedIn !== generation) return null;
    if (error || !user?.id) throw new Error('Not authenticated');
    cachedUserId = user.id;
    return user.id;
  })();
  const current = { generation: startedIn, promise };
  flight = current;
  // Only clear OUR record: a newer generation's flight must survive this one finishing late.
  const clear = () => {
    if (flight === current) flight = null;
  };
  promise.then(clear, clear);
  return promise;
}

/** Return cached userId or fetch and cache it. Throws if not authenticated. */
export async function getCachedUserId(): Promise<string> {
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (cachedUserId) return cachedUserId;
    const startedIn = generation;
    const running = flight && flight.generation === startedIn ? flight.promise : null;
    const id = await (running ?? fetchUserId(startedIn));
    // Adoption can also land after fetchUserId resolved but before this continuation.
    if (startedIn !== generation) continue;
    if (id !== null) return cachedUserId ?? id;
  }
  throw new Error('Not authenticated');
}

/** Clear the cache and orphan any request still in the air. Call synchronously whenever the
 *  adopted Auth actor may have changed (different UID, sign-out). */
export function invalidateUserIdCache(): void {
  generation += 1;
  cachedUserId = null;
  flight = null;
}
